/**
 * View Data Fetching Hooks
 *
 * Hooks for fetching and managing view definitions and view data.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  ViewDefinition,
  ViewCreate,
  ViewCreateFull,
  ViewUpdate,
  ViewData,
  ViewRowCreate,
  ViewRowUpdate,
} from "../types";
import {
  subscribeWorkspaceObjectChanges,
  workspaceChangeAffects,
} from "../services/workspaceObjectChanges";

const VIEWS_API_BASE = "/api/workspace/myvault/views";
const VIEW_ROWS_API_BASE = "/api/workspace/myvault/view-rows";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Hook to fetch all view definitions for a workspace.
 */
export const useViews = (workspaceId: string) => {
  const [views, setViews] = useState<ViewDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchViews = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        VIEWS_API_BASE,
        "fetch views",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setViews(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Views fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch views";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["view"])) fetchViews();
      }),
    [workspaceId, fetchViews],
  );

  return {
    views,
    loading,
    error,
    refresh: fetchViews,
  };
};

/**
 * Hook to fetch a single view definition by ID.
 */
export const useView = (
  workspaceId: string,
  viewId: number | string | null,
) => {
  const [view, setView] = useState<ViewDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchView = useCallback(async () => {
    if (!workspaceId || viewId === null) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Use /full endpoint to get view with fields, joins, and filters
      const response = await authFetchRef.current(
        `${VIEWS_API_BASE}/${viewId}/full`,
        "fetch view",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setView(response);
      setError(null);
    } catch (err) {
      console.error("View fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch view";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, viewId]);

  useEffect(() => {
    fetchView();
  }, [fetchView]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["view"], viewId)) fetchView();
      }),
    [workspaceId, viewId, fetchView],
  );

  return {
    view,
    loading,
    error,
    refresh: fetchView,
  };
};

/**
 * Hook for view mutations (create, update, delete).
 */
export const useViewMutations = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  const createView = useCallback(
    async (data: ViewCreate | ViewCreateFull): Promise<ViewDefinition> => {
      setLoading(true);
      setError(null);
      try {
        // Use /full endpoint to include fields, joins, filters in creation
        const response = await authFetch(
          `${VIEWS_API_BASE}/full`,
          "create view",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create view";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const updateView = useCallback(
    async (
      viewId: number | string,
      data: ViewUpdate,
    ): Promise<ViewDefinition> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${VIEWS_API_BASE}/${viewId}`,
          "update view",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update view";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const deleteView = useCallback(
    async (viewId: number | string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(`${VIEWS_API_BASE}/${viewId}`, "delete view", {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete view";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  return {
    createView,
    updateView,
    deleteView,
    loading,
    error,
  };
};

/**
 * Hook to fetch view data (rows) with optional pagination and filtering.
 */
export const useViewData = (
  workspaceId: string,
  viewId: number | string | null,
  options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    sort?: { field: string; direction: "ASC" | "DESC" }[];
    autoFetch?: boolean;
  },
) => {
  const [data, setData] = useState<ViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const autoFetch = options?.autoFetch !== false;

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchData = useCallback(async () => {
    if (!workspaceId || viewId === null) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (options?.page !== undefined) params.set("page", String(options.page));
      if (options?.pageSize !== undefined)
        params.set("page_size", String(options.pageSize));
      if (options?.filters)
        params.set("filters", JSON.stringify(options.filters));
      if (options?.sort) params.set("sort", JSON.stringify(options.sort));

      const queryString = params.toString();
      const url = `${VIEW_ROWS_API_BASE}/${viewId}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await authFetchRef.current(url, "fetch view data", {
        headers: getWorkspaceHeaders(workspaceId),
      });
      setData(response);
      setError(null);
    } catch (err) {
      console.error("View data fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch view data";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [
    workspaceId,
    viewId,
    options?.page,
    options?.pageSize,
    options?.filters,
    options?.sort,
  ]);

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [fetchData, autoFetch]);

  return {
    data,
    loading,
    error,
    refresh: fetchData,
  };
};

/**
 * Hook for view row mutations (create, update, delete).
 */
export const useViewRowMutations = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  const createRow = useCallback(
    async (
      viewId: number | string,
      data: ViewRowCreate,
    ): Promise<Record<string, unknown>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${VIEW_ROWS_API_BASE}/${viewId}/rows`,
          "create row",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create row";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const updateRow = useCallback(
    async (
      viewId: number | string,
      rowId: string | number,
      data: ViewRowUpdate,
    ): Promise<Record<string, unknown>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${VIEW_ROWS_API_BASE}/${viewId}/rows/${rowId}`,
          "update row",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update row";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const deleteRow = useCallback(
    async (viewId: number | string, rowId: string | number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(
          `${VIEW_ROWS_API_BASE}/${viewId}/rows/${rowId}`,
          "delete row",
          { method: "DELETE", headers: getWorkspaceHeaders(workspaceId) },
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete row";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  return {
    createRow,
    updateRow,
    deleteRow,
    loading,
    error,
  };
};
